/** Human-readable byte count (1 decimal under 10, none above). */
export function formatBytes(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes < 0) return "—";
    if (bytes < 1024) return `${bytes} B`;
    const units = ["KB", "MB", "GB", "TB"];
    let v = bytes;
    let i = -1;
    do { v /= 1024; i++; } while (v >= 1024 && i < units.length - 1);
    return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
}

/** Full date + time, e.g. "13 Sep 2026, 10:42". */
export function formatDateTime(iso: string | null | undefined): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/**
 * Google-Drive style compact date for list columns: a time for today,
 * "12 Sep" for this year, "12 Sep 2024" otherwise.
 */
export function formatShortDate(iso: string | null | undefined): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
        return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    }
    if (d.getFullYear() === now.getFullYear()) {
        return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
    }
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const clock = (d: Date) => d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

/**
 * Compact list-row date: "Today 14:05", "Yesterday", "Mon" within the week,
 * "12 Sep" this year, "12 Sep 2024" before.
 */
export function formatListDate(iso: string | null | undefined): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const now = new Date();
    const days = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
    if (days === 0) return `Today ${clock(d)}`;
    if (days === 1) return "Yesterday";
    if (days > 1 && days < 7) return d.toLocaleDateString(undefined, { weekday: "short" });
    if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** Details-pane date: "Today 14:05", "Yesterday 09:12", else the full date + time. */
export function formatDetailDate(iso: string | null | undefined): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const days = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86_400_000);
    if (days === 0) return `Today ${clock(d)}`;
    if (days === 1) return `Yesterday ${clock(d)}`;
    return formatDateTime(iso);
}

/** "just now", "5 minutes ago", "yesterday", then a short date after a week. */
export function formatRelative(iso: string | null | undefined): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    const seconds = Math.round((d.getTime() - Date.now()) / 1000);
    const abs = Math.abs(seconds);
    if (abs < 45) return "just now";
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
    if (abs < 3600) return rtf.format(Math.round(seconds / 60), "minute");
    if (abs < 86_400) return rtf.format(Math.round(seconds / 3600), "hour");
    if (abs < 7 * 86_400) return rtf.format(Math.round(seconds / 86_400), "day");
    return formatShortDate(iso);
}

/** Day heading for grouped lists: "Today", "Yesterday", "Monday, 7 September 2026". */
export function formatDayHeading(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "Earlier";
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString(undefined, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: d.getFullYear() === today.getFullYear() ? undefined : "numeric"
    });
}

/** "3 items" / "1 item". */
export function plural(n: number, word: string, pluralWord = `${word}s`): string {
    return `${n} ${n === 1 ? word : pluralWord}`;
}
